import { Checkbox } from '@crowdex/ui/components/checkbox';
import {
  type CheckpointConflict,
  type CheckpointRevertResolution,
  checkpointConflictText,
} from '@crowdex/workspace-protocol';
import { useId } from 'react';

/**
 * What a revert decides for the files someone else may have changed: left as they are unless the
 * user chose to put them back too.
 */
export function conflictResolution(
  conflicts: readonly CheckpointConflict[],
  overwrite: boolean,
): CheckpointRevertResolution {
  const paths = conflicts.map((conflict) => conflict.path);
  return overwrite ? { overwrite: paths } : { keep: paths };
}

/**
 * The files an undo or a restore would put back over work it did not do (another conversation's,
 * the user's), each with why, and the choice to put them back anyway. Leaving them is the default.
 */
export function CheckpointConflicts({
  conflicts,
  overwrite,
  onOverwriteChange,
}: {
  conflicts: readonly CheckpointConflict[];
  overwrite: boolean;
  onOverwriteChange: (overwrite: boolean) => void;
}) {
  const id = useId();
  if (conflicts.length === 0) return null;
  return (
    <section
      aria-label="Files changed by someone else"
      className="grid gap-2 rounded-xl border border-hairline bg-control/40 px-3.5 py-3"
      data-checkpoint-conflicts
    >
      <p className="font-medium text-foreground text-sm">
        {conflicts.length === 1
          ? '1 file may hold work this conversation did not do'
          : `${conflicts.length} files may hold work this conversation did not do`}
      </p>
      <ul className="grid max-h-40 gap-1.5 overflow-y-auto">
        {conflicts.map((conflict) => (
          <li className="min-w-0" key={conflict.path}>
            <span
              className="block truncate font-mono text-foreground text-xs"
              title={conflict.path}
            >
              {conflict.path}
            </span>
            <span className="type-meta block text-muted-foreground">
              {checkpointConflictText(conflict.reason)}
            </span>
          </li>
        ))}
      </ul>
      <label className="flex cursor-pointer items-start gap-2.5 text-sm" htmlFor={id}>
        <Checkbox
          checked={overwrite}
          className="mt-0.5"
          id={id}
          onCheckedChange={(checked) => onOverwriteChange(checked === true)}
        />
        <span>
          Put {conflicts.length === 1 ? 'it' : 'them'} back too
          <span className="type-meta block text-muted-foreground">
            Otherwise {conflicts.length === 1 ? 'it stays' : 'they stay'} as{' '}
            {conflicts.length === 1 ? 'it is' : 'they are'}
          </span>
        </span>
      </label>
    </section>
  );
}
